import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  FormField,
  Heading,
  Icon,
  Label,
  LabeledControl,
  Modal,
  Paragraph,
  Radio,
  Select,
  Text,
} from '@zillow/constellation-10';
import { IconErrorFilled } from '@zillow/constellation-icons';
import {
  applicationUtils,
  ErrorMessageConstants,
  MAX_NUMBER_OF_APPLICATION_PERMISSIONS,
  PERMISSION_STATUS,
  RentalManagerUserManagementApplicationPermissionWebApiHandler_transferApplicationManager,
  transferRequirements,
  useRentalManagerUser,
} from '@zg-rentals/rental-manager-env';
import { IntermediateStep } from '../../components/IntermediateStep';
import { styledErrorParagraphStyles } from './StyledComponents';
import { useApplicationPermissions } from '../../hooks/useApplicationPermissions';
import { ModalFooter } from '@zg-rentals/rental-revenue-components';

const STEPS = {
  initial: 'initial',
  permissionUpdate: 'permissionUpdate',
  confirmation: 'confirmation',
};

const PERMISSION_ACTIONS = {
  keep: 'keep',
  giveUp: 'giveUp',
};

export const PermissionTransferModal = ({
  setShowPermissionTransferModal = () => undefined,
}: {
  setShowPermissionTransferModal: (value: boolean) => void;
}) => {
  const [step, setStep] = useState(STEPS.initial);
  const [error, setError] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);
  const { user } = useRentalManagerUser();
  const { getUsersWithApplicationPermissionDetails, companyAdmins, applicationManager, users } =
    useApplicationPermissions();

  const {
    formState: { errors },
    register,
    handleSubmit,
    setValue,
    watch,
  } = useForm({ mode: 'onBlur' });
  const userTokenField = register('userToken');
  const userTokenValue = watch(userTokenField.name);
  const selectedCompanyAdmin = companyAdmins.find((companyAdmin) => companyAdmin.userToken === userTokenValue);
  const keepPermissionsField = register('keepPermissions', {
    validate: (value) => {
      const usersWithApplicationPermissions = users.filter((u) => u.permissionStatus === PERMISSION_STATUS.granted);
      const hasNewApplicationManagerApplicationPermission = usersWithApplicationPermissions.find(
        (u) => u.userToken === userTokenValue,
      );
      if (
        value === PERMISSION_ACTIONS.keep &&
        usersWithApplicationPermissions.length >= MAX_NUMBER_OF_APPLICATION_PERMISSIONS &&
        !hasNewApplicationManagerApplicationPermission
      ) {
        return 'The maximum number of users already have application permissions assigned.';
      }
      return true;
    },
  });

  const onCancel = () => {
    if (step === STEPS.initial) {
      setShowPermissionTransferModal(false);
    } else if (step === STEPS.permissionUpdate) {
      setError('');
      setStep(STEPS.initial);
    }
  };

  // @ts-expect-error – submit handler receives untyped form data
  const onSubmit = (data) => {
    if (step === STEPS.initial) {
      setStep(STEPS.permissionUpdate);
    } else if (step === STEPS.permissionUpdate) {
      if (!selectedCompanyAdmin || !applicationManager) return;
      const { userToken, keepPermissions } = data;
      const { firstName, lastName } = selectedCompanyAdmin;
      const body = {
        userManagementCompanyId: user.userManagementCompanyId,
        oldManagerUserToken: applicationManager.userToken,
        newManagerUserToken: userToken,
        firstName,
        lastName,
        keepApplicationPermission: keepPermissions === PERMISSION_ACTIONS.keep,
      };
      setIsTransferring(true);
      return RentalManagerUserManagementApplicationPermissionWebApiHandler_transferApplicationManager(body)
        .then(() => {
          setStep(STEPS.confirmation);
          return;
        })
        .catch((errorResponse) => {
          setError(errorResponse.message || ErrorMessageConstants.DEFAULT_ERROR);
        })
        .finally(() => setIsTransferring(false));
    }
  };

  const handleClose = () => {
    if (step === STEPS.confirmation) {
      getUsersWithApplicationPermissionDetails()
        .then(() => setShowPermissionTransferModal(false))
        .catch(() => undefined);
    } else {
      setShowPermissionTransferModal(false);
    }
  };

  const filteredCompanyAdmins = companyAdmins.filter(
    (admin) => admin.userToken !== user.userToken && admin.firstName && admin.lastName,
  );
  const managerOptions = filteredCompanyAdmins.map((option) => ({
    label: `${option.firstName} ${option.lastName}`,
    value: option.userToken,
    meta: option.userEmail,
  }));

  const fullName = applicationUtils.getName(selectedCompanyAdmin || {});

  return (
    <Modal
      open
      header={<Heading level={6}>Permission transfer</Heading>}
      footer={
        step !== STEPS.confirmation && (
          <ModalFooter
            onCancel={onCancel}
            onSubmit={handleSubmit(onSubmit)}
            cancelText={step === STEPS.initial ? 'Cancel' : 'Back'}
            cancelType="button"
            submitText={step === STEPS.permissionUpdate ? 'Save' : 'Next'}
            isSubmitDisabled={step === STEPS.initial && !selectedCompanyAdmin}
            isSubmitting={isTransferring}
            errorMessage={error}
            hasError={!!error}
          />
        )
      }
      onOpenChange={(open) => !open && handleClose()}
      body={
        <>
          {step === STEPS.initial && (
            <>
              <Heading level={5} css={{ marginBottom: 'default' }}>
                Transfer the application manager role?
              </Heading>
              <Paragraph css={{ marginBlockEnd: 'sm' }}>
                If you make someone else application manager, you&apos;ll no longer be able to assign application
                permissions.
              </Paragraph>
              <FormField
                css={{ marginBottom: 'sm' }}
                label={<Label>Application manager</Label>}
                control={
                  <Select
                    fluid
                    size="md"
                    value={userTokenValue}
                    onChange={(e) => setValue(userTokenField.name, e.target.value)}
                  >
                    <option value="">Select member</option>
                    {managerOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                }
              />
              <Text textStyle="body-sm-bold" asChild>
                <p>Who can I transfer the manager role to?</p>
              </Text>
              <Text textStyle="body-sm" css={{ marginBlockEnd: 'sm' }} asChild>
                <p>{transferRequirements}</p>
              </Text>
            </>
          )}
          {step === STEPS.permissionUpdate && (
            <>
              <Heading level={5}>Your permission update</Heading>
              <Paragraph css={{ marginTop: 'default', marginBottom: 'default' }}>
                When you transfer the application manager role to another user, you&apos;ll no longer be able to access
                applications.
              </Paragraph>
              <Paragraph css={{ marginBlockEnd: 'sm' }}>
                To access applications, you must be one of the 5 users assigned application permissions.
              </Paragraph>
              <LabeledControl
                label={<Label>Give up application permissions</Label>}
                control={<Radio {...keepPermissionsField} value={PERMISSION_ACTIONS.giveUp} defaultChecked={true} />}
              />
              <LabeledControl
                label={<Label>Keep application permissions</Label>}
                control={<Radio {...keepPermissionsField} value={PERMISSION_ACTIONS.keep} />}
              />
              {typeof errors.keepPermissions?.message === 'string' && (
                <Box css={{ display: 'flex', marginBlockStart: 'sm' }}>
                  <Icon render={<IconErrorFilled />} css={{ color: 'red600' }} size="sm" />
                  <Paragraph className={styledErrorParagraphStyles}>{errors.keepPermissions?.message}</Paragraph>
                </Box>
              )}
            </>
          )}
          {step === STEPS.confirmation && (
            <IntermediateStep headingLevel={4} headingText="You transferred the application manager role">
              <Paragraph css={{ marginBlockEnd: 'sm' }}>
                {`${fullName} `}
                is the new application manager and will receive all applications for all properties in this account.
              </Paragraph>
              <Paragraph css={{ marginBlockEnd: 'sm' }}>
                Their name will be displayed as the person viewing applications.
              </Paragraph>
            </IntermediateStep>
          )}
        </>
      }
    />
  );
};

export default PermissionTransferModal;
